import { useState } from "react";
import * as api from "../api.js";
import { confirmar, pedirTexto } from "@/componentes/pergunta.tsx";
import { useAcoes, useCasoAtual, useCelular, useEstado, useOcupado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { chaveDaEtiqueta } from "../etiquetas.js";
import { esperaDaConversa, pendenciaAperta, prazoEmPalavras, progressoDoFunil, sinaisDoCaso } from "../caso.js";
import { ICONES, Icone, tinta } from "../visual.jsx";
import { Botao, Cortina, Etiqueta, Faixa, GradeDeWidgets, Gravou, LinhaDoPainel, MenuDeAcoes, Secao, Widget } from "./comuns.jsx";
import Copiloto from "./Copiloto.jsx";
import ListaDeReunioes from "./ListaDeReunioes.jsx";
import WidgetDaPeticao from "./WidgetDaPeticao.jsx";
import CampoDaFicha from "./CampoDaFicha.jsx";
import EtiquetasRapidas, { useEtiquetasDoCaso } from "./EtiquetasDoCaso.jsx";

/*
 * A ficha do caso — uma coluna, na ordem em que se decide.
 *
 * O que ela era: nove blocos do mesmo peso, 2400px de rolagem, e as duas listas
 * de "o que fazer agora" (a do servidor e a da IA) uma embaixo da outra, com o
 * mesmo desenho e conteúdo parecido — quem abria não sabia qual clicar. Mais
 * abaixo, a linha do tempo do funil repetia ponto por ponto o trilho que a
 * conversa já desenha na largura inteira, e a FICHA imprimia a chave do banco
 * como rótulo (`funcao_cliente`) com o valor em monoespaçado terracota
 * alinhado à direita, inclusive parágrafos de sessenta palavras.
 *
 * O que ela é: AGORA (o que o servidor manda fazer, e só depois o que a IA
 * sugere), ETAPA (uma linha, que abre para mover), PENDÊNCIAS, FICHA (rótulo do
 * esquema, valor formatado, texto longo que abre), REUNIÕES e o histórico
 * fechado no pé. A identidade saiu daqui e virou o cabeçalho preso no alto.
 */

/*
 * Os estados que o servidor aceita (`CONFERENCIA`, em `src/rotas/documentos.ts`).
 * A lista é curta e fechada — é vocabulário de conferência, não campo do CRM.
 */
const CONFERENCIA = ["aprovado", "faltante", "ilegivel", "dispensado"];

/*
 * Como cada estado é DITO na tela. O valor gravado continua o do servidor.
 *
 * `recebido` existe em `src/fluxo.ts` e não está na lista de escolhas daqui —
 * por isso o `??` nos selos.
 */
const ROTULO_CONFERENCIA = {
  aprovado: "Aprovado",
  recebido: "Chegou, a conferir",
  faltante: "Está faltando",
  ilegivel: "Ilegível",
  dispensado: "Dispensado",
};

/*
 * A cor de cada estado de conferência.
 *
 * Os dois que exigem DECISÃO (chegou e ninguém disse se serve; ilegível, que é
 * pedir de novo) têm cor própria — antes caíam no mesmo cinza de `dispensado`,
 * que é o estado em que não há mais nada a fazer, e quem varria a lista pulava
 * justamente eles.
 */
const CORES_DO_ESTADO = {
  aprovado: ["color-mix(in oklab, var(--color-verde-fundo) 14%, transparent)", "var(--color-verde-fundo)"],
  recebido: ["color-mix(in oklab, var(--color-ambar) 16%, transparent)", "var(--color-ambar-escuro)"],
  faltante: ["color-mix(in oklab, var(--color-ambar) 16%, transparent)", "var(--color-ambar-escuro)"],
  ilegivel: ["color-mix(in oklab, var(--color-terra) 15%, transparent)", "var(--color-terra-escura)"],
  dispensado: ["color-mix(in oklab, var(--color-foreground) 6%, transparent)", "var(--color-tinta-suave)"],
};

/*
 * O selo de origem da pendência.
 *
 * A origem é o que diz ONDE se resolve — prazo na esteira, documento com o
 * cliente, régua na cobrança do dia. As chaves são as de `src/pendencias.ts`;
 * a cor é a mesma que a marca daquele assunto já tem na mesa.
 */
const ORIGEM = {
  prazo: ["PRAZO", "color-mix(in oklab, var(--color-terra) 15%, transparent)", "var(--color-terra-escura)"],
  tarefa: ["TAREFA", "color-mix(in oklab, var(--color-ambar) 16%, transparent)", "var(--color-ambar-escuro)"],
  regua: ["RÉGUA", "color-mix(in oklab, var(--color-azul) 15%, transparent)", "var(--color-azul)"],
  documento: ["DOCUMENTO", "color-mix(in oklab, var(--color-verde-fundo) 15%, transparent)", "var(--color-verde-fundo)"],
  reuniao: ["REUNIÃO", "color-mix(in oklab, var(--color-foreground) 8%, transparent)", "var(--color-tinta-suave)"],
};

/** O título de seção com contador — a mesma linha em toda a coluna. */
/*
 * O mapa PASSO → SEÇÃO, e por que ele não é por chave de etapa.
 *
 * O painel mostrava as sete seções abertas, na mesma ordem, em qualquer etapa:
 * 2974px de rolagem numa vista de 900, e "o que fazer" começando a 610px de
 * altura — atrás da identidade repetida, do gravador de reunião (num caso
 * parado em documentos) e do cartão da IA. Quem abre um caso não procura "as
 * informações do cliente": procura o que fazer agora.
 *
 * O que destaca a seção é a AÇÃO do passo que o servidor manda
 * (`pauta.passos[].acao`, em `src/produtividade.ts`), e não a chave da etapa. Dois
 * motivos, e o segundo é o que decide: a etapa é configurável em tempo de
 * execução (mapa por chave viraria mentira no funil que o escritório montar
 * amanhã), e o servidor JÁ decide o que importa agora — ler a etapa aqui para
 * concluir a mesma coisa criaria uma segunda verdade sobre o mesmo assunto.
 *
 * `acao` é lista fechada de código, como `gatilho` nos fluxos: ação nova entra
 * aqui, ou o painel destaca a ficha (que é o padrão e nunca é errado, só é
 * genérico).
 */
/* O ícone de cada sinal. Nome que não existe em `ICONES` derruba a TELA (o
   `Icone` faz spread do desenho), e `visual.test.jsx` lê este objeto. */
const ICONE_DO_SINAL = {
  documentos: ICONES.confere,
  pendencias: ICONES.relogio,
  ficha: ICONES.pessoa,
  reunioes: ICONES.calls,
};

const SECAO_DO_PASSO = {
  conferir_documento: "documentos",
  aprovar_checklist: "documentos",
  cobrar_documentos: "documentos",
  aplicar_reuniao: "reunioes",
  extrair_reuniao: "reunioes",
  minutar: "peca",
  aplicar_ficha: "ficha",
  sugerir_ficha: "ficha",
  /* Confirmar a tese MOVE o caso: quem decide isso quer ver a etapa, não a ficha. */
  confirmar_tese: "etapa",
  /* `responder` e `aprovar_fila` não destacam nada no painel de propósito: um se
     resolve na conversa (à esquerda) e o outro em Automação (outra tela). */
};

/* A peça `Secao_` (título + triângulo + conteúdo dobrado) morreu em 27/08 com a
   regra "widget que aparece quando precisa, nunca colapsável". Ela era o que
   fazia o painel ter sete linhas permanentes dizendo que havia coisa dentro de
   coisa que não mudou. O conteúdo dela vive aberto no pé da coluna, e o sinal
   correspondente rola até ele. */

/* Onde a seção para ao rolar até ela: logo abaixo do cabeçalho preso, pela
   altura MEDIDA dele (`CabecalhoDoPainel` escreve a variável). Os 76px fixos de
   antes eram a altura de um cabeçalho que já não tinha 76. */
const ENCOSTO = "scroll-mt-[calc(var(--altura-cabecalho-painel,56px)_+_12px)]";

function Titulo({ children, contador, acao, id }) {
  return (
    <div data-secao={id} className={`mt-5 mb-2 flex items-baseline gap-2 ${ENCOSTO}`}>
      <Secao espaco="0">{children}</Secao>
      {contador != null ? <span className="font-mono text-selo text-tinta-fraca">{contador}</span> : null}
      {acao ? <span className="ml-auto">{acao}</span> : null}
    </div>
  );
}

/* -------------------------------------------------------------- o agora */

/**
 * Um passo do servidor: o que fazer, o que ele faz, e o botão que faz.
 *
 * O cartão inteiro é o alvo — a linha de detalhe explica a consequência, e
 * consequência é o que a pessoa lê antes de apertar. A seta à direita é o
 * afordance: sem ela, um cartão claro no meio de outros cartões claros não
 * anuncia que é clicável.
 */
/**
 * Um passo do AGORA. O PRIMEIRO é grande; os outros são linha.
 *
 * Eram todos do mesmo tamanho, e com três na tela nenhum era o próximo — a
 * pessoa lia três cartões iguais e escolhia. O servidor já devolve os passos em
 * ordem de urgência (`src/produtividade.ts`): o primeiro é a resposta à pergunta
 * "o que eu faço agora", e o desenho passa a dizer isso. Os outros continuam à
 * mão, do tamanho de uma alternativa.
 */
function Passo({ passo, ocupado, aoTocar, principal = false }) {
  if (!principal)
    return (
      /* Parece BOTÃO: borda, fundo e alvo. Era texto cinza com uma seta de 12px
         sobre fundo transparente — "Enviar o contrato para assinatura" lia como
         legenda do cartão de cima. É a mesma linha-botão da ETAPA e dos sinais
         (44, raio 14): uma linha que leva a algum lugar é UM controle. */
      <div className="mb-2">
        <LinhaDoPainel
          aoTocar={aoTocar}
          disabled={ocupado}
          data-dica={passo.detalhe}
          icone={passo.confirmar ? ICONES.cadeado : undefined}
          valor={passo.rotulo}
        />
      </div>
    );

  return (
    <button
      type="button"
      onClick={aoTocar}
      disabled={ocupado}
      data-dica={passo.detalhe}
      /* Terracota claro, largura inteira: é o botão primário do painel, e painel
         com dois primários não tem primário. No ponteiro o terra FORTALECE — o
         cinza do `.lin` apagava justamente o passo que mais importa. É cartão:
         afunda .985, não os .97 do botão. */
      className="group mb-2 flex w-full cursor-pointer flex-col items-start gap-1.5 rounded-cartao border border-terra/30 bg-[color-mix(in_oklab,var(--color-terra)_7%,var(--color-card))] p-[14px] text-left transition-[border-color,background-color,scale] duration-[var(--t-curta)] ease-entra hover:border-terra/60 hover:bg-[color-mix(in_oklab,var(--color-terra)_11%,var(--color-card))] active:transform-none active:scale-[.985] disabled:cursor-wait disabled:border-dashed disabled:bg-muted"
    >
      <span className="flex w-full items-center gap-2.5">
        <span className="flex size-6 flex-none items-center justify-center rounded-etiqueta bg-terra text-sobre-terra">
          <Icone de={passo.confirmar ? ICONES.cadeado : ICONES.avancar} tamanho={14} />
        </span>
        <span className="min-w-0 flex-1 font-serif text-[17px] leading-[1.25] text-balance">{passo.rotulo}</span>
      </span>
      {passo.detalhe ? <span className="block text-apoio leading-[1.5] text-tinta-media">{passo.detalhe}</span> : null}
    </button>
  );
}

/** Um documento esperando decisão, com as quatro respostas a um toque. */
function ItemDoChecklist({ item, podeConferir }) {
  const { conferirDocumento } = useAcoes();
  const cores = CORES_DO_ESTADO[item.estado] ?? CORES_DO_ESTADO.dispensado;

  return (
    /* A conferência é a ação mais repetida da mesa — quatro por caso, todo dia.
       É a que mais precisa confirmar onde aconteceu. */
    <Gravou chave={`checklist:${item.id}`} classe="flex items-center justify-between gap-2.5 py-[5px]">
      <span className="min-w-0 flex-1 truncate text-apoio">{item.documento}</span>
      {podeConferir && item.id ? (
        <MenuDeAcoes
          rotulo={`Conferir ${item.documento}`}
          titulo="Conferir este documento"
          legenda={item.documento}
          largura="200px"
          classe="inline-flex h-6 flex-none cursor-pointer items-center rounded-etiqueta border px-[9px] text-[12px] leading-none font-semibold"
          itens={CONFERENCIA.map((estado) => ({
            chave: estado,
            rotulo: ROTULO_CONFERENCIA[estado] ?? estado,
            aoTocar: () => conferirDocumento(item.id, estado),
          }))}
          estilo={({ aberto }) =>
            `background:${cores[0]};color:${cores[1]};border-color:${aberto ? "var(--color-borda-controle)" : "transparent"}`
          }
        >
          {ROTULO_CONFERENCIA[item.estado] ?? item.estado}
        </MenuDeAcoes>
      ) : (
        <Etiqueta cores={cores}>{ROTULO_CONFERENCIA[item.estado] ?? item.estado}</Etiqueta>
      )}
    </Gravou>
  );
}

/**
 * AGORA — uma pilha só, e o servidor manda nela.
 *
 * Eram DUAS listas adjacentes com o mesmo desenho: "PRÓXIMAS AÇÕES SUGERIDAS"
 * (do modelo) e "PRÓXIMO PASSO" (do banco, com rota e permissão). Diziam quase a
 * mesma coisa com palavras diferentes — "Pedir os documentos que faltam" e
 * "Pedir o que falta" — e nada na tela dizia qual das duas executava algo. Aqui
 * o que EXECUTA vem primeiro e aberto; o que SUGERE vem depois, dobrado, dito
 * como sugestão. Quando não há passo do servidor, a sugestão abre sozinha: aí
 * ela é a única coisa que há.
 */
function Agora() {
  const { caso, ia } = useEstado();
  const ocupado = useOcupado();
  const { executarPasso, decidirFila, acaoDoCopiloto } = useAcoes();
  const { ajustar } = useNavegacao();
  const pode = usePode();
  const podeConferir = pode("documentos:conferir");
  const passos = (caso.pauta?.passos ?? []).filter((p) => pode(p.permissao));
  const fila = caso.pauta?.fila ?? [];
  const sugestoes = ia?.acoes ?? [];
  /* O passo de conferir documento não é um POST: a rota confere UM item e pede o
     estado. Quem tem o id de cada um é o checklist — o passo dá o rótulo, a
     permissão e a contagem, e a decisão acontece item por item, aqui. */
  const aConferir = passos.some((p) => p.acao === "conferir_documento")
    ? (caso.checklist ?? []).filter((i) => i.estado === "recebido")
    : [];

  if (!passos.length && !fila.length && !sugestoes.length) return null;

  return (
    <>
      <Titulo id="agora" contador={passos.length + fila.length || null}>
        AGORA
      </Titulo>

      {/* O PRIMEIRO passo é o grande; do segundo em diante são linhas. É a
          diferença entre "o que eu faço agora" e "o que mais dá para fazer" —
          três cartões iguais na tela não respondem a primeira pergunta. */}
      {passos.map((p, n) =>
        p.acao === "conferir_documento" ? (
          <div
            key={p.acao}
            className={
              n === 0
                ? "mb-2 rounded-cartao border border-ambar/40 bg-ambar/10 p-[14px]"
                : "mb-2 rounded-cartao border border-ambar/34 bg-ambar/9 p-[11px_12px]"
            }
          >
            <div className={n === 0 ? "font-serif text-[17px] leading-[1.25]" : "text-corpo font-semibold"}>{p.rotulo}</div>
            <div className="mt-[3px] text-apoio leading-[1.45] text-tinta-suave">{p.detalhe}</div>
            <div className="mt-1.5 border-t border-t-foreground/8 pt-1">
              {aConferir.map((i) => (
                <ItemDoChecklist key={i.id ?? i.documento} item={i} podeConferir={podeConferir} />
              ))}
            </div>
            {/* Decidir com o documento na tela, um por um: a pastilha ao lado do
                nome serve para a correção solta ("este eu marquei errado"); para
                conferir uma fila inteira, o lugar é a tela grande. */}
            {podeConferir && aConferir.length ? (
              <div className="mt-2">
                <Botao
                  rotulo={`Conferir ${aConferir.length} na tela`}
                  destaque
                  titulo="Abre um por um, com o arquivo grande e as três decisões"
                  aoTocar={() => ajustar({ conferir: true })}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <Passo key={p.acao} passo={p} principal={n === 0} ocupado={ocupado === "passo"} aoTocar={() => executarPasso(p)} />
        ),
      )}

      {fila.map((item) => (
        <div key={item.id} className="mb-2 rounded-cartao border border-azul/30 bg-azul/8 p-[11px_12px]">
          <div className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 text-corpo font-semibold">{item.tarefa}</span>
            <Etiqueta cores={["color-mix(in oklab, var(--color-azul) 16%, transparent)", "var(--color-azul)"]} peso={700}>
              AUTOMAÇÃO
            </Etiqueta>
          </div>
          <div className="mt-1 text-apoio leading-[1.45] text-tinta-suave">
            Espera aprovação para gravar fora do CRM{item.pedido_por ? ` — pedido por ${item.pedido_por}` : ""}.
          </div>
          {pode("automacao:aprovar") ? (
            <div className="mt-2 flex gap-1.5">
              <Botao rotulo="Aprovar" titulo="Deixar o agente executar" aoTocar={() => decidirFila(item, true)} destaque />
              {/* guarda-ok: pergunta em `decidirFila` (estado.jsx) — ela sabe o nome da tarefa. */}
              <Botao rotulo="Cancelar" titulo="Tirar da fila sem executar" aoTocar={() => decidirFila(item, false)} />
            </div>
          ) : (
            <div className="mt-[7px] text-apoio text-muted-foreground">
              Quem aprova é um advogado ou sócio; o acesso se libera em Equipe e acessos.
            </div>
          )}
        </div>
      ))}

      {/*
       * As sugestões da IA: LINHAS, e só quando o servidor não tem passo.
       *
       * Eram um colapsável — "SUGESTÕES DO COPILOTO · 3", com triângulo — e ele
       * caía na regra nova: linha permanente dizendo que há coisa dentro. Pior,
       * dizia isso ACIMA do que se faz, competindo com o passo que executa.
       *
       * Com passo do servidor na tela, elas não aparecem: o passo é o que o CRM
       * MANDA fazer (tem rota e permissão) e a sugestão é o que o modelo ACHA —
       * duas listas parecidas, e a pessoa sem saber qual clicar, é o defeito que
       * a coluna já teve. Sem passo nenhum, elas são a única coisa que há, e aí
       * entram abertas.
       */}
      {sugestoes.length && !passos.length ? (
        <div className="mt-1">
          <div className="mb-1.5 flex items-center gap-1.5 text-selo font-bold tracking-[.08em] text-tinta-fraca">
            <Icone de={ICONES.brilho} tamanho={14} />O QUE A IA SUGERE
          </div>
          {sugestoes.map((acao) => (
            <button
              key={acao.titulo}
              type="button"
              onClick={() => acaoDoCopiloto(acao.marca)}
              className="lin mb-2 flex min-h-toque w-full cursor-pointer items-start gap-2 rounded-cartao border border-dashed border-borda-controle bg-transparent p-[10px_12px] text-left hover:border-tinta-fraca"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-corpo leading-[1.35] font-semibold">{acao.titulo}</span>
                <span className="mt-[3px] block text-apoio leading-[1.45] text-tinta-suave">{acao.detalhe}</span>
              </span>
              <Icone de={ICONES.avancar} tamanho={16} cor="var(--color-tinta-fraca)" />
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------- a etapa */

/**
 * A etapa: UMA linha que abre para mover.
 *
 * Ficava aberta na coluna — dez linhas com bolinha e "mover" — repetindo o
 * trilho do processo, que já desenha o caminho inteiro na largura toda e já
 * move. O que só existe aqui é escolher a etapa PELO NOME, e isso é decisão
 * rara: fica atrás de um toque, com o funil inteiro numa folha.
 *
 * Botões e não `RadioGroup`: no rádio a SETA muda o valor, e `onValueChange`
 * disparava a pergunta de mover a cada tecla. Mover é escrita no funil — só
 * por clique ou Enter, e sempre com `confirmar()`.
 */
function EtapaDoCaso() {
  const { etapas, caso } = useEstado();
  const { moverEtapa } = useAcoes();
  const [aberta, setAberta] = useState(false);
  const stage = caso?.registro?.stage;
  if (!stage || !etapas?.length) return null;

  const p = progressoDoFunil(etapas, stage, caso?.trilha);
  if (p.indice < 0) return null;
  const atual = p.passos[p.indice];

  const mover = async (etapa) => {
    /* A folha fecha antes da pergunta: duas caixas empilhadas disputam o foco. */
    setAberta(false);
    if (await confirmar({ titulo: `Mover para "${etapa.rotulo}"?`, acao: "Mover" })) moverEtapa(etapa.chave);
  };

  return (
    /* Mover a etapa é a escrita mais consequente da ficha: ela dispara régua,
       fila e prazo. O verde do recibo é o que confirma onde aconteceu. */
    <Gravou chave={`etapa:${caso.registro.id}`}>
      <div className="mt-3">
        <LinhaDoPainel
          aoTocar={() => setAberta(true)}
          aria-label={`Etapa: ${atual.rotulo}${p.ha ? `, ${p.ha}` : ""}. Mover para outra etapa`}
          rotulo="Etapa"
          valor={
            <>
              {atual.rotulo}
              {p.ha ? <span className="font-normal text-tinta-fraca">{` · ${p.ha}`}</span> : null}
            </>
          }
        >
          <span className="flex-none text-apoio text-tinta-suave">mover</span>
        </LinhaDoPainel>
      </div>
      {aberta ? (
        <Cortina aoFechar={() => setAberta(false)} tamanho="estreita" z={44} titulo="Mover o caso de etapa">
          {p.passos.map((etapa, i) => {
            const feito = etapa.estado === "feita";
            const agora = i === p.indice;
            const bolinha = (
              <span
                className={`flex size-[14px] flex-none items-center justify-center rounded-full border-2 text-selo font-bold text-sobre-terra ${
                  agora ? "border-terra/30 bg-terra" : feito ? "border-verde bg-verde" : "border-borda-controle bg-background"
                }`}
              >
                {feito ? "✓" : ""}
              </span>
            );
            /* A etapa atual não é botão: é onde o caso já está. */
            return agora ? (
              <div key={etapa.chave} aria-current="step" className="flex min-h-toque items-center gap-2.5 rounded-controle px-2 py-[5px]">
                {bolinha}
                <span className="min-w-0 flex-1 truncate text-corpo font-bold text-foreground">{etapa.rotulo}</span>
                {p.ha ? <span className="flex-none text-apoio text-tinta-fraca">{p.ha}</span> : null}
              </div>
            ) : (
              <button
                key={etapa.chave}
                type="button"
                onClick={() => void mover(etapa)}
                className="lin flex min-h-toque w-full cursor-pointer items-center gap-2.5 rounded-controle border-none bg-transparent px-2 py-[5px] text-left"
              >
                {bolinha}
                <span
                  className={`min-w-0 flex-1 truncate text-corpo ${feito ? "font-medium text-tinta-suave" : "text-tinta-fraca"}`}
                >
                  {etapa.rotulo}
                </span>
                <span className="flex-none text-apoio font-semibold text-tinta-fraca">mover</span>
              </button>
            );
          })}
        </Cortina>
      ) : null}
    </Gravou>
  );
}

/* --------------------------------------------------------- as pendências */

/**
 * Uma pendência do caso — prazo, documento, régua ou combinado de reunião.
 *
 * Nada é calculado aqui. `dias` e `atraso` vêm de `pendencias.listar()`, com o
 * relógio do SERVIDOR: somar data no navegador colapsava qualquer atraso na
 * palavra "vencido", e um prazo vencido há quarenta dias lia igual ao de ontem.
 *
 * O limiar do destaque continua na tela porque destaque é pintura, não regra.
 */
function Pendencia({ pendencia: p }) {
  const { concluirPendencia, conferirDocumento } = useAcoes();
  const { ir } = useNavegacao();
  const podeConferir = usePode()("documentos:conferir");
  const [rotulo, fundo, letra] = ORIGEM[p.origem] ?? ORIGEM.reuniao;
  const aperta = pendenciaAperta(p);

  return (
    <div
      className={`mb-2 rounded-cartao border p-[10px_12px] ${aperta ? "border-ambar/34 bg-ambar/10" : "border-border bg-card"}`}
    >
      {/*
       * DUAS linhas, e não três coisas numa: o selo e o prazo em cima, o título
       * embaixo na largura inteira.
       *
       * Em fileira única a coluna de 300px dava ~110px para o título depois do
       * selo ("RÉGUA") e do prazo ("vencido há 2 dia(s)"), e como o título é o
       * único que encolhe (`min-w-0 flex-1`) ele era esmagado até quebrar UMA
       * PALAVRA POR LINHA: "Cobrar / documentos / de / CLEIDE / APARECIDA /
       * BONFIM". Selo e prazo são curtos e cabem juntos; o título é o que
       * precisa de linha.
       */}
      <div className="flex items-baseline gap-2">
        <Etiqueta cores={[fundo, letra]} peso={700}>
          {rotulo}
        </Etiqueta>
        <span
          className={`min-w-0 flex-1 truncate text-right font-mono text-apoio font-bold ${
            p.atraso > 0 ? "text-terra" : aperta ? "text-ambar-escuro" : "text-verde"
          }`}
        >
          {prazoEmPalavras(p)}
        </span>
      </div>
      <div className="mt-[3px] text-corpo leading-[1.35] font-semibold text-balance">{p.titulo}</div>
      <div className="mt-[5px] text-apoio leading-[1.45] text-tinta-suave">{p.detalhe}</div>
      <div className="mt-[7px] flex flex-wrap items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate text-apoio text-tinta-fraca">{p.para ?? "de quem pegar"}</span>
        {/* Só tarefa conclui aqui: o `id` da pendência é o da tabela de origem, e
            prazo de peça se resolve na esteira — por isso o link em vez do botão. */}
        {p.origem === "tarefa" ? (
          <>
            <Botao rotulo="Concluir" tamanho="mini" titulo="Dar esta tarefa por feita" aoTocar={() => concluirPendencia(p)} />
            {/* `p.id` É o id da tarefa (contrato de `src/pendencias.ts`), então dá
                para apontar A tarefa, e não a lista. */}
            <Botao
              rotulo="No quadro"
              tamanho="mini"
              titulo="Abrir esta tarefa em Tarefas e agenda"
              aoTocar={() => ir(`/tarefas?tarefa=${p.id}`)}
            />
          </>
        ) : null}
        {p.origem === "prazo" && p.link ? <Botao rotulo="Abrir a peça" tamanho="mini" titulo={p.detalhe} aoTocar={() => ir(p.link)} /> : null}
        {p.origem === "reuniao" && p.link ? <Botao rotulo="Ver a reunião" tamanho="mini" titulo={p.detalhe} aoTocar={() => ir(p.link)} /> : null}
        {/* Documento se resolve aqui: o `id` da pendência É a linha do checklist
            (contrato de `src/pendencias.ts`). */}
        {p.origem === "documento" && podeConferir ? (
          <MenuDeAcoes
            rotulo={`Conferir ${p.titulo}`}
            titulo={p.detalhe}
            legenda={p.titulo}
            largura="200px"
            classe="inline-flex h-mini flex-none cursor-pointer items-center rounded-controle border bg-card px-2.5 text-apoio font-semibold text-tinta-media hover:bg-muted hover:text-foreground"
            itens={CONFERENCIA.map((estado) => ({
              chave: estado,
              rotulo: ROTULO_CONFERENCIA[estado] ?? estado,
              aoTocar: () => conferirDocumento(p.id, estado),
            }))}
            estilo={({ aberto }) =>
              `border-color:${aberto ? "var(--color-terra)" : "var(--color-borda-controle)"}`
            }
          >
            Conferir
          </MenuDeAcoes>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- a ficha */

/** Quantos documentos do checklist já não voltam para a mão de ninguém. */
function ResumoDosDocumentos({ caso }) {
  const itens = caso.checklist ?? [];
  if (!itens.length) return null;
  const prontos = itens.filter((i) => i.estado === "aprovado" || i.estado === "dispensado").length;
  const pct = Math.round((prontos / itens.length) * 100);

  return (
    <div className="mb-1.5 flex items-center gap-2.5 rounded-cartao border border-border bg-card p-[10px_12px]">
      <span className="min-w-0 flex-1">
        <span className="block text-apoio font-semibold">
          {prontos} de {itens.length} documento(s) resolvido(s)
        </span>
        <span className="mt-[5px] flex h-1 overflow-hidden rounded-full bg-foreground/12">
          <span className="block h-full rounded-full bg-verde transition-[width] duration-[var(--t-media)] ease-movendo" style={{ width: `${pct}%` }} />
        </span>
      </span>
      <span className="flex-none font-mono text-selo font-semibold text-verde-fundo">{pct}%</span>
    </div>
  );
}

/**
 * A ficha inteira, na ordem do esquema — e o que falta nela.
 *
 * "Ficha vazia." era beco: a minuta transforma cada campo em branco em
 * "[A CONFERIR]", e a resposta já estava no servidor (`pauta.ficha.faltam` diz
 * quais campos a peça vai furar). O aviso do que falta vale MESMO com a ficha
 * meio cheia, que é o caso comum — antes só aparecia quando ela estava vazia.
 */
function FichaDoCliente() {
  const { caso, campos: esquema } = useEstado();
  const { executarPasso } = useAcoes();
  const pode = usePode();
  /* UMA regra para a ficha inteira: ela nasce completa, menos o que o SISTEMA
     preenche e ainda está vazio. O recorte "só o que a peça usa" morreu junto com
     a gaveta — campo criado pelo escritório parecia não ter sido criado, e o
     toggle escondia justamente o campo novo. `tudo` hoje é só "mostrar também os
     automáticos em branco", para quem precisa conferir que não há nada lá. */
  const [tudo, setTudo] = useState(false);

  const dados = caso.registro?.dados ?? {};
  const preenchido = (v) => v !== "" && v != null;
  const ordem = (chave) => (esquema ?? []).find((c) => c.chave === chave)?.ordem ?? 999;

  /* O que a PEÇA precisa e não tem. `pauta.ficha.faltam` vem do servidor com os
     campos que a minuta imprimiria como "[A CONFERIR]" — eles entram na lista
     como linha vazia, para serem preenchidos AQUI. Antes só existiam numa frase
     ("3 campos viram [A CONFERIR]") que não dava para clicar. */
  const faltam = (caso.pauta?.ficha?.faltam ?? []).filter((c) => !preenchido(dados[c]));
  const cheios = Object.keys(dados).filter((c) => preenchido(dados[c]));
  /* A ordem é a do escritório (`fields.ordem`), e não a ordem em que as chaves
     caíram no JSON. Campo livre (que não está no esquema) vai para o fim. */
  const daPeca = [...new Set([...cheios, ...faltam])].toSorted((a, b) => ordem(a) - ordem(b) || a.localeCompare(b));
  /* "Todos" é o esquema inteiro: é o que permite preencher um campo que ninguém
     nunca preencheu neste caso — antes ele simplesmente não existia na tela. */
  const todos = [...new Set([...daPeca, ...(esquema ?? []).map((c) => c.chave)])].toSorted(
    (a, b) => ordem(a) - ordem(b) || a.localeCompare(b),
  );
  /* Campo que o SISTEMA preenche (número do processo, pasta, campanha, criativo)
     só aparece quando TEM valor: a ficha aberta na coluna mostrava sete linhas
     "preencher" que ninguém preenche à mão — e cada uma delas convida a digitar
     por cima do que a integração vai gravar. Cheio, ele aparece como qualquer
     outro, porque corrigir o que a máquina errou continua sendo trabalho de
     gente. O toggle abaixo continua abrindo TUDO, inclusive esses. */
  const automatico = (chave) => Number((esquema ?? []).find((c) => c.chave === chave)?.automatico) === 1;
  /* O campo que a peça precisa aparece SEMPRE, automático ou não: a frase de
     cima diz que ele está marcado abaixo, e a marca tem de estar lá. */
  const visiveis = tudo ? todos : todos.filter((c) => preenchido(dados[c]) || !automatico(c) || faltam.includes(c));
  const passo = (caso.pauta?.passos ?? []).find(
    (p) => (p.acao === "aplicar_ficha" || p.acao === "sugerir_ficha") && pode(p.permissao),
  );

  return (
    <>
      {/* Sem título interno: a seção "Ficha do cliente" já se apresenta. Dois
          títulos iguais um debaixo do outro é a repetição que a coluna passou o
          dia inteiro perdendo. */}
      {faltam.length ? (
        /* Frase em cima, botão embaixo: lado a lado, numa coluna de 300px o
           botão comia a largura e a frase quebrava palavra por linha. */
        <Faixa
          tom="aviso"
          icone={ICONES.risco}
          titulo={faltam.length === 1 ? "1 campo em branco" : `${faltam.length} campos em branco`}
          classe="mb-2"
        >
          {faltam.length === 1 ? "Vira" : "Viram"} "[A CONFERIR]" na peça —{" "}
          {faltam.length === 1 ? "está marcado" : "estão marcados"} abaixo.
          {passo ? (
            <div className="mt-2">
              <Botao
                rotulo={passo.rotulo}
                titulo={passo.detalhe}
                tamanho="mini"
                aoTocar={() => executarPasso(passo)}
                destaque
              />
            </div>
          ) : null}
        </Faixa>
      ) : null}

      <div className="rounded-cartao border border-border bg-card px-3 py-1">
        {visiveis.map((chave, i) => (
          <CampoDaFicha
            key={chave}
            chave={chave}
            valor={dados[chave]}
            campo={(esquema ?? []).find((c) => c.chave === chave)}
            primeiro={i === 0}
            falta={faltam.includes(chave)}
          />
        ))}
        {todos.length > visiveis.length || tudo ? (
          <button
            type="button"
            onClick={() => setTudo(!tudo)}
            className="min-h-toque w-full cursor-pointer border-t border-t-foreground/8 bg-transparent text-apoio font-semibold text-tinta-suave hover:text-foreground"
          >
            {tudo ? "esconder o que o sistema preenche" : `mostrar os outros ${todos.length - visiveis.length} campos`}
          </button>
        ) : null}
        {!visiveis.length ? (
          <div className="py-2 text-apoio leading-[1.5] text-tinta-fraca">
            Ficha vazia. Abra "mostrar os outros campos" e preencha o que a peça imprime: tese, empresa, admissão, saída e
            salário.
          </div>
        ) : null}
      </div>

      {/* Criar campo é ESQUEMA, e por isso fica FORA do cartão dos campos e
          atrás da permissão de configurar: quem atende preenche, quem configura
          inventa a linha nova. */}
      {pode("config:editar") ? <CriarCampo /> : null}
    </>
  );
}

/**
 * Os tipos que a ficha oferece ao criar campo — e por que não são os sete.
 *
 * `CampoDaFicha` desenha por tipo, e só desenha estes cinco: `booleano` cairia
 * no `<input type=text>` (um campo de sim/não onde se digita "sim" é pior que
 * nenhum) e `selecao` precisa de uma lista de opções, que é cadastro de
 * escritório e não decisão de quem está com o cliente na linha. Os dois
 * continuam existindo no domínio (`TIPOS_DE_CAMPO`) para o importador e o MCP.
 *
 * A ordem é a da frequência: quase todo campo do escritório é texto ou data.
 */
const TIPOS_QUE_A_FICHA_CRIA = [
  { id: "texto", rotulo: "Texto", detalhe: "Uma linha — nome, número, empresa" },
  { id: "texto_longo", rotulo: "Texto longo", detalhe: "Parágrafo — resumo, observação" },
  { id: "data", rotulo: "Data", detalhe: "Abre o calendário do aparelho" },
  { id: "numero", rotulo: "Número", detalhe: "Valor, quantidade, salário" },
  { id: "telefone", rotulo: "Telefone", detalhe: "Com DDD" },
];

/**
 * Criar um campo do esquema SEM sair do caso.
 *
 * O escritório descobre que precisa de um campo no meio de um atendimento ("e a
 * data do aviso prévio?"), e até aqui o caminho era: sair da conversa, achar a
 * tela de configuração, inventar a chave `data_do_aviso_previo` e voltar. Na
 * prática o dado ia para uma nota na trilha — onde não filtra, não conta e a
 * peça não lê.
 *
 * O TIPO primeiro e o nome depois, e não o contrário: o tipo é uma lista curta
 * (um menu, um toque) e o nome é digitação. Perguntando o nome primeiro, a
 * pessoa escreve "Data do aviso" e a caixa de texto some antes de ela poder
 * dizer que é data — e campo criado com o tipo errado é campo que ninguém
 * conserta, porque consertar é outra tela.
 *
 * O campo vale para TODOS os casos: é esquema, não dado deste cliente. A frase
 * do menu diz isso, porque criar quinze campos para quinze clientes é como a
 * ficha vira ilegível para o escritório inteiro.
 */
function CriarCampo() {
  const { avisar, carregarMesa } = useAcoes();
  const criar = async (tipo) => {
    const rotulo = await pedirTexto({
      titulo: `Campo novo de ${tipo.rotulo.toLowerCase()}`,
      texto: "O nome que vai aparecer na ficha de TODOS os casos — é esquema do escritório, não dado deste cliente.",
      acao: "Criar o campo",
      convite: "Data do aviso prévio",
    });
    if (!rotulo) return;
    try {
      const campo = await api.criarCampo({ rotulo, tipo: tipo.id });
      /* A mesa relê porque é ela que traz o esquema (`/api/mesa` → `campos`): sem
         isto o campo existe no banco e não aparece na linha até alguém recarregar. */
      await carregarMesa();
      avisar(`Campo "${campo.rotulo}" criado — preencha na linha dele.`);
    } catch (e) {
      avisar(e.message);
    }
  };

  return (
    <MenuDeAcoes
      rotulo="Criar um campo novo na ficha"
      titulo="Que tipo de campo?"
      legenda="Vale para todos os casos"
      ancora="bottom start"
      largura="272px"
      classe="lin mt-2 flex min-h-toque w-full cursor-pointer items-center justify-center gap-1.5 rounded-cartao border border-dashed border-borda-controle bg-transparent text-apoio font-semibold text-tinta-suave hover:text-foreground"
      itens={TIPOS_QUE_A_FICHA_CRIA.map((t) => ({
        chave: t.id,
        rotulo: t.rotulo,
        detalhe: t.detalhe,
        icone: ICONES.somar,
        aoTocar: () => void criar(t),
      }))}
    >
      <Icone de={ICONES.somar} tamanho={14} />
      campo novo
    </MenuDeAcoes>
  );
}

/* ------------------------------------------------------------ passagens */

/** Por onde o caso passou. Fechado: é consulta, não leitura do dia. */
function Passagens() {
  const { caso } = useEstado();
  const passagens = (caso?.trilha ?? []).filter((e) => e.tipo === "etapa" || e.tipo === "nota").slice(0, 8);
  if (!passagens.length) return null;

  /* PLANO, sem dobra: a seção "Histórico" já está aberta no pé da coluna, e um
     colapsável aqui seria pedir dois toques para ver o que o primeiro prometeu. */
  return (
    <div>
      {passagens.map((e) => (
        <div key={e.id} className="border-b border-b-foreground/8 py-2 text-corpo leading-[1.5] text-tinta-media last:border-b-0">
          {e.texto ?? `${e.de ?? ""} → ${e.para ?? ""}`}{" "}
          <span className="font-mono text-apoio text-tinta-fraca">{e.criado_em?.slice(5, 16) ?? ""}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- a coluna */

/**
 * A coluna do caso — o que fazer primeiro, e o resto a um toque.
 *
 * O que ela era: sete seções ABERTAS, na mesma ordem, em qualquer etapa —
 * copiloto, agora, etapa, pendências, ficha, reuniões, histórico. Medido em
 * 27/08 com a mesa semeada: **2974px de rolagem numa vista de 900**, e o que a
 * pessoa precisa FAZER começando a 610px de altura, atrás da identidade
 * repetida (o nome e o telefone estão na coluna ao lado, a 300px de distância),
 * de um gravador de reunião num caso parado em documentos, e de um cartão de IA
 * de 310px. Quem abre um caso não vem ler a ficha do cliente: vem descobrir o
 * que fazer.
 *
 * O que ela é, em três regras:
 *
 *   1. **AGORA é o primeiro e o único sempre aberto.** É o passo que o servidor
 *      manda, com o botão que o executa.
 *   2. **Uma seção a mais abre sozinha: a do PASSO.** `SECAO_DO_PASSO` diz qual
 *      — documentos quando há o que conferir, reuniões quando há alinhamento
 *      para aplicar, a peça quando é hora de minutar. O resto fica fechado, com
 *      a linha dizendo o que tem dentro.
 *   3. **A leitura da IA é uma linha, não um cartão.** Ela continua sendo a
 *      primeira coisa depois do que fazer (é "o que a IA está entendendo do
 *      caso"), e abre inteira num toque.
 *
 * Fechado não é escondido: cada linha fechada tem contador e resumo, e custa
 * 34px em vez de 400. O que estava a três rolagens de distância passa a estar a
 * um toque — e o que a etapa torna urgente já está aberto quando a coluna abre.
 */
export default function FichaDoCaso() {
  const { caso, campos } = useEstado();
  const atual = useCasoAtual();
  /* As etiquetas do caso e a cor de cada uma saem do mesmo gancho que o painel
     de etiquetar usa — duas leituras do catálogo é como uma delas envelhece. */
  const { minhas: tags, cores: coresDasTags } = useEtiquetasDoCaso(atual?.id);
  const { ajustar, secaoDoPainel } = useNavegacao();
  const pode = usePode();
  const celular = useCelular();
  const podeConferirDocs = pode("documentos:conferir");
  if (!atual || !caso) return null;

  const checklist = caso.checklist ?? [];
  const gravacoes = caso.gravacoes ?? [];
  /* Os passos que ESTA pessoa pode dar — o mesmo recorte do AGORA. Sem ele o
     destaque saía de um passo que o AGORA nem mostra, e a coluna escondia o
     widget de uma seção que ninguém estava resolvendo. */
  const passos = (caso.pauta?.passos ?? []).filter((p) => pode(p.permissao));
  /* Com o passo de conferir no AGORA, os documentos "chegou, a conferir" já
     estão lá, cada um com o menu de conferência: a seção não os repete. */
  const conferindoNoAgora = passos.some((p) => p.acao === "conferir_documento");
  const docsDaSecao = conferindoNoAgora ? checklist.filter((i) => i.estado !== "recebido") : checklist;
  /* O que a coluna já diz noutro lugar não entra em PENDÊNCIAS: o documento
     cobrado É a linha do checklist (mesmo id, mesmo menu), e a tarefa da reunião
     marcada é o cartão da reunião. O sino continua com as duas. */
  const haMarcada = (caso.reunioes ?? []).some((r) => r.estado === "marcada");
  const pendencias = (caso.pendencias ?? []).filter(
    (p) =>
      !(p.origem === "documento" && checklist.length) &&
      !(p.origem === "tarefa" && haMarcada && String(p.titulo ?? "").startsWith("Reunião de alinhamento — ")),
  );
  const dados = caso.registro?.dados ?? {};
  const preenchidos = (campos ?? []).filter((c) => dados[c.chave] !== "" && dados[c.chave] != null).length;
  /* A seção que o PRIMEIRO passo pede — é ele que o AGORA mostra em cima, e a
     ordem dos passos no servidor já é a de urgência. Sem passo nenhum, a ficha:
     é o que se olha quando não há nada a fazer, e nunca é resposta errada. */
  const destaque = SECAO_DO_PASSO[passos[0]?.acao] ?? "ficha";

  const faltam = caso.pauta?.ficha?.faltam ?? [];
  const passagens = (caso.trilha ?? []).filter((e) => e.tipo === "etapa" || e.tipo === "nota");

  /* Os sinais em ordem de urgência (a regra é de `caso.js`, com teste). O
     primeiro só fica GRANDE quando trava — dois cartões grandes competindo é o
     defeito que esta coluna já teve, em escala menor. */
  /* `?painel=reunioes` entra na conta: a sugestão "Marcar o alinhamento" só
     aparece quando NÃO há reunião e endereça esta seção — sem isto o alvo não
     está montado e o toque não faz nada (`Painel.jsx` rola até `[data-secao]`). */
  const sinaisPara = secaoDoPainel === "reunioes" ? "reunioes" : passos.length ? destaque : null;
  const { sinais, mudos } = sinaisDoCaso({ caso: { ...caso, pendencias }, faltam, secaoDoPasso: sinaisPara });
  const destacado = sinais[0]?.u >= 80 ? sinais[0] : null;
  const resto = destacado ? sinais.slice(1) : sinais;
  const espera = esperaDaConversa(caso.mensagens ?? []);
  /* Os sinais levam ao BLOCO, que está aberto logo abaixo — nada de folha por
     cima da conversa para ver o que já está na coluna. */
  const irAte = (chave) =>
    document.querySelector(`[data-bloco="${chave}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="px-4 pb-8">
      {/*
       * 0. ETIQUETAR — ação recorrente, endereço fixo.
       *
       * Etiqueta era widget na grade, e widget muda de lugar: a grade se
       * reordena por urgência e o que se faz TODO DIA não pode andar pela
       * coluna. Ela também não é sinal — não vence, não muda sozinha e não
       * espera ninguém —, então não disputa a fila com prazo e documento. Fica
       * aqui, sempre no mesmo lugar, com as etiquetas do caso à mão.
       */}
      {/*
       * As duas portas: a etiqueta e o "+" abrem o PAINEL ancorado (pôr, tirar,
       * criar), não mais o modal. O modal cobria a mesa inteira com um véu para
       * o gesto de um toque — e escondia justamente o caso que se estava lendo.
       * Renomear, pintar e apagar continuam nele, pelo pé do painel: valem para
       * todos os casos e perguntam antes.
       */}
      <div className="mb-2.5 flex flex-wrap items-center gap-1">
        {tags.map((t) => (
          <EtiquetasRapidas
            key={t}
            alvo={atual.id}
            rotulo={`Etiqueta ${t} — pôr, tirar ou trocar`}
            classe="lin cursor-pointer rounded-full border-none bg-transparent p-0"
          >
            <Etiqueta cores={tinta(t, coresDasTags.get(chaveDaEtiqueta(t)))}>{t}</Etiqueta>
          </EtiquetasRapidas>
        ))}
        <EtiquetasRapidas
          alvo={atual.id}
          rotulo="Pôr, tirar e criar etiqueta"
          classe="alvo lin flex h-6 cursor-pointer items-center gap-1 rounded-full border border-dashed border-borda-controle bg-transparent px-[9px] text-[12px] font-semibold text-tinta-suave hover:bg-foreground/8 hover:text-tinta-media"
        >
          <Icone de={ICONES.etiqueta} tamanho={14} />
          etiqueta
        </EtiquetasRapidas>
      </div>

      {/* 1. O QUE FAZER. Sem título de seção e sem dobra: é a razão da coluna. */}
      <Agora />

      {/* 2. O que a IA entendeu — uma linha, que abre. */}
      <Copiloto />

      {/*
       * 3. OS WIDGETS: o estado do caso de relance, no idioma de iPadOS.
       *
       * Eram títulos em caixa alta com o conteúdo solto embaixo — lê bem num
       * documento e mal numa coluna de 300px que se varre com o olho vinte vezes
       * por dia: nada tem borda, tudo tem o mesmo peso, e o que importa não se
       * distingue do que só está ali.
       *
       * Cada widget responde UMA pergunta com um número no canto ("2/4", "há
       * 12d") e o cartão inteiro é o alvo — o dedo acerta, e o ponteiro também.
       * Grade de duas colunas, e o que precisa de linha inteira pede `largo`.
       *
       * O que está AQUI e não atrás da porta é o que a etapa torna urgente. O
       * resto continua a um toque, com o mesmo conteúdo de antes.
       */}
      {/*
       * 3. OS SINAIS — o que MUDOU e o que TRAVA, em ordem de urgência.
       *
       * A grade era fixa: seis widgets na ordem do código, e quatro deles
       * dizendo que não havia nada ("nenhum pedido", "nada em aberto",
       * "Qualificado", "1 etiqueta"). Ausência ocupando a área de presença
       * empurra para baixo o que importa; e a ordem do código não é a ordem do
       * dia, porque o servidor já manda os passos ordenados por urgência e a
       * coluna ignorava isso abaixo do AGORA.
       *
       * Agora quem decide a ordem é `sinaisDoCaso` (`caso.js`, com teste), o
       * primeiro sinal que TRAVA fica grande e sozinho na linha, e o que não tem
       * nada a dizer vira uma linha no pé. A petição continua widget próprio: ela
       * tem barra de estado e duas contas que nenhum outro tem.
       */}
      <div className="mt-3">
        {/* O sinal que TRAVA é o mesmo Widget da grade, grande e sozinho: era
            um cartão refeito à mão com a mesma forma e outra medida (valor 28
            num, 22 no outro), e dois desenhos para a mesma coisa envelhecem
            separados. */}
        {destacado ? (
          <div className="mb-2 grid">
            <Widget
              titulo={destacado.titulo}
              valor={destacado.valor}
              tom={destacado.tom === "acao" ? "acao" : "ambar"}
              aoTocar={() => irAte(destacado.chave)}
              dica={destacado.apoio}
            >
              <span className="flex items-baseline gap-2 text-apoio leading-[1.35]">
                <span className="min-w-0 flex-1 text-tinta-suave">
                  {destacado.apoio}
                  {destacado.relogio ? <span className="text-tinta-fraca">{` · ${destacado.relogio}`}</span> : null}
                </span>
                <span
                  className={`flex flex-none items-center gap-1 font-semibold whitespace-nowrap ${destacado.tom === "acao" ? "text-terra" : "text-ambar-escuro"}`}
                >
                  {destacado.verbo}
                  <Icone de={ICONES.avancar} tamanho={14} />
                </span>
              </span>
            </Widget>
          </div>
        ) : null}

        <GradeDeWidgets>
          {/* A petição atravessa: ela tem barra de estado e um nome longo. Veio
              do cabeçalho da conversa, onde era um ícone que abria uma tela. */}
          <WidgetDaPeticao />

          {/* Os outros sinais viraram LINHA e saíram da grade de cartões: o
              conteúdo deles está aberto logo abaixo, então o cartão de 84px
              repetia em número o que a seção diz por extenso. A linha mantém o
              que só ela tem — a URGÊNCIA (a cor) e o relógio — e leva até lá. */}
          {resto.length ? (
            <div className="col-span-2 flex flex-col gap-2">
              {resto.map((sinal) => (
                <LinhaDoPainel
                  key={sinal.chave}
                  aoTocar={() => irAte(sinal.chave)}
                  aria-label={`${sinal.titulo}, ${sinal.valor}. ${sinal.apoio}`}
                  data-dica={sinal.apoio}
                  icone={ICONE_DO_SINAL[sinal.chave]}
                  tom={sinal.tom === "acao" ? "acao" : undefined}
                  rotulo={sinal.titulo}
                  valor={
                    <>
                      <span className={`font-mono font-bold ${sinal.tom === "acao" ? "text-terra" : "text-foreground"}`}>
                        {sinal.valor}
                      </span>
                      <span className="font-normal text-tinta-fraca">{` · ${sinal.apoio}`}</span>
                    </>
                  }
                />
              ))}
            </div>
          ) : null}

          {/* A conversa esperando é sinal de tempo, e o tempo era o que faltava:
              "com o cliente há 22h" decide insistir, ligar ou devolver, e não
              estava escrito em lugar nenhum da coluna. Escuro porque é a única
              coisa aqui que não é trabalho NOSSO parado. */}
          {espera?.comOCliente && espera.horas >= 4 ? (
            <Widget
              icone={ICONES.relogio}
              titulo="Com o cliente"
              marca="espera"
              largo
              valor={espera.ha}
              tom="escuro"
              /* Só no celular, onde a coluna FLUTUA sobre a conversa: fechar é
                 voltar para ela. Na mesa larga a coluna é fixa, e `painel: 0`
                 a sumia sem porta de volta. */
              aoTocar={celular ? () => ajustar({ painel: 0 }) : undefined}
              dica="A última palavra foi nossa — a bola está com ele"
            >
              <span className="text-apoio leading-[1.35] text-sobre-escuro/60">sem resposta desde a nossa última mensagem</span>
            </Widget>
          ) : null}
        </GradeDeWidgets>

        {/* A ausência inteira numa linha: era quatro widgets de tamanho cheio
            dizendo que não havia nada. Não é botão de propósito — não há o que
            fazer com o que está em dia. */}
        {mudos.length ? (
          <div className="mt-2 flex min-h-toque items-center gap-2 rounded-cartao border border-dashed border-foreground/12 px-3 py-2 text-apoio text-tinta-fraca">
            <Icone de={ICONES.confere} tamanho={14} />
            <span className="min-w-0 flex-1">
              {mudos.map((m, n) => (n ? m : m[0].toUpperCase() + m.slice(1))).join(", ")}: nada em aberto
            </span>
          </div>
        ) : null}

        {/*
         * O CASO INTEIRO, ABERTO. A gaveta "Tudo sobre o caso" morreu.
         *
         * Ela contava o que guardava ("4 doc · 4 pend") e ainda assim escondia:
         * saber que existem quatro documentos não diz QUAIS chegaram, e a
         * resposta custava um toque, uma folha por cima da conversa e um ✕ para
         * voltar. Numa coluna que a pessoa varre vinte vezes por dia, o dado
         * atrás do clique é dado que ninguém lê.
         *
         * Os widgets acima continuam sendo o relance de urgência — mas agora
         * ROLAM até a seção em vez de abrir folha: mesmo conteúdo, sem véu e sem
         * volta.
         */}
        <div className="mt-1">
          {/* `data-secao` mora na SEÇÃO, e só nela: o sinal lá em cima tinha o
              mesmo atributo, e `?painel=` rolava até o primeiro que achava — o
              botão, não o conteúdo. */}
          {checklist.length ? (
            <section data-bloco="documentos" data-secao="documentos" className={ENCOSTO}>
              <Titulo contador={`${checklist.length}`}>Documentos</Titulo>
              <ResumoDosDocumentos caso={caso} />
              {docsDaSecao.map((item) => (
                <ItemDoChecklist key={item.id ?? item.documento} item={item} podeConferir={podeConferirDocs} />
              ))}
            </section>
          ) : null}

          {pendencias.length ? (
            <section data-bloco="pendencias" data-secao="pendencias" className={ENCOSTO}>
              <Titulo contador={`${pendencias.length}`}>Prazos e pendências</Titulo>
              {pendencias.map((x) => (
                <Pendencia key={x.chave} pendencia={x} />
              ))}
            </section>
          ) : null}

          <section data-bloco="etapa" data-secao="etapa" className={ENCOSTO}>
            <EtapaDoCaso />
          </section>

          <section data-bloco="reunioes" data-secao="reunioes" className={ENCOSTO}>
            <Titulo contador={gravacoes.length ? `${gravacoes.length}` : null}>Reuniões</Titulo>
            <ListaDeReunioes />
          </section>
        </div>

        {/*
         * 4. A FICHA INTEIRA, aberta, no pé da coluna.
         *
         * Ela morava só atrás de "Tudo sobre o caso", e isso escondia o campo que
         * o próprio escritório acabou de criar: quem inventa "observações" quer
         * escrever nele durante a conversa, não procurar onde ele foi parar. Fica
         * no PÉ e não no topo porque os 140px do AGORA são o que a coluna responde
         * primeiro — e o widget FICHA continua em cima, dizendo o que falta.
         */}
        <section data-bloco="ficha" data-secao="ficha" className={ENCOSTO}>
          {/* Preenchidos sobre o total: o tamanho do esquema sozinho ("30") não
              dizia nada sobre ESTE caso. */}
          <Titulo contador={campos?.length ? `${preenchidos}/${campos.length}` : null}>Ficha do cliente</Titulo>
          <FichaDoCliente />
        </section>

        {passagens.length ? (
          <section data-bloco="historico" className={ENCOSTO}>
            {/* A lista mostra as oito mais novas; o contador conta o que aparece. */}
            <Titulo contador={passagens.length > 8 ? `8 de ${passagens.length}` : `${passagens.length}`}>Histórico</Titulo>
            <Passagens />
          </section>
        ) : null}
      </div>
    </div>
  );
}
